import type { AllergensCopy } from '../types/allergens';

export const allergensFr: AllergensCopy = {
  seo: {
    title: 'Allergènes de l’UE\u00A0: les 14 et la loi, expliqués',
    description:
      'Les 14 allergènes de l’UE et ce que la loi demande aux restaurateurs, en langage clair. Apprenez à déclarer les allergènes sur votre menu, et découvrez les sources cachées qui piègent les cuisines.',
  },
  hero: {
    eyebrow: 'Guide pratique pour restaurateurs',
    headingPre: 'Les 14 allergènes de l’UE, expliqués en',
    headingScribble: 'langage clair',
    body: 'Découvrez quels allergènes la loi couvre, où ils se cachent, et comment le dire clairement à vos clients. Ce guide vous accompagne à travers les plats comme les règles, sans jargon juridique.',
    factAllergenCount: '14 allergènes',
    factRegulation: 'Règlement (UE) n° 1169/2011',
    factReadTime: '6 min de lecture',
    imageAlt: 'Une table de restaurant avec un menu QR code SafestMenu',
  },
  intro: {
    leadPre: 'Si vous servez à manger dans l’UE, nommer ces 14 allergènes est à la fois',
    leadScribble1: 'une obligation légale',
    leadMid: 'et',
    leadScribble2: 'un geste d’hospitalité',
    leadPost: '.',
    body: 'Ces allergènes se cachent dans bien plus d’endroits que la plupart des menus ne l’admettent. Cette page explique les 14, la loi qui les encadre, et comment les déclarer clairement - simple et concret, pour agir dès aujourd’hui.',
    stats: [
      { big: '14', small: 'substances sur une liste fixe' },
      { big: 'Chaque', small: 'restaurant, café et bar de l’UE' },
      { big: 'Nommés', small: 'clairement, jamais laissés au hasard' },
    ],
  },
  whyItMatters: {
    eyebrow: 'Pourquoi c’est important',
    heading: 'Des clients en sécurité sont ceux qui reviennent.',
    imageAlt: 'Des clients qui regardent un menu ensemble à une table de restaurant',
    cards: [
      {
        title: 'Une réaction peut être grave',
        text: 'Pour certains clients, une trace cachée n’est pas une question de préférence - elle peut les envoyer à l’hôpital.',
      },
      {
        title: 'La confiance, c’est l’hospitalité',
        text: 'Faites-le bien, et les clients se détendent. Des informations claires sur les allergènes font partie d’un accueil chaleureux, pas de la paperasse.',
      },
      {
        title: 'C’est la loi',
        text: 'Le restaurant est l’exploitant du secteur alimentaire responsable, donc l’obligation vous incombe à vous, et non à votre fournisseur.',
      },
    ],
  },
  theFourteen: {
    eyebrow: 'La référence',
    heading: 'Découvrez les quatorze',
    intro: 'Ce sont les allergènes nommés à l’annexe II du règlement (UE) n° 1169/2011. C’est une liste fixe et fermée - vous ne choisissez pas lesquels suivre, et tout plat qui en contient un doit le signaler.',
    gotchas: {
      heading: '3 pièges qui surprennent les cuisines',
      items: [
        'L’huile d’arachide compte toujours comme arachide, raffinée ou non. Seule l’huile de soja entièrement raffinée est exemptée - n’étendez jamais cette exemption à l’huile d’arachide, de cacahuète ou d’arachis.',
        'L’avoine est naturellement sans gluten, mais presque toujours contaminée lors de la mouture. Considérez l’avoine ordinaire comme contenant du gluten, sauf si elle est certifiée sans gluten.',
        'La lécithine de soja (E322) se trouve dans une grande partie des chocolats, pâtes à tartiner et produits de boulangerie - vérifiez les fiches fournisseurs, pas seulement les ingrédients visibles.',
      ],
    },
  },
  legislation: {
    eyebrow: 'La loi, en bref',
    heading: 'Règlement (UE) n° 1169/2011',
    paragraphs: [
      'Les règles proviennent du règlement (UE) n° 1169/2011, souvent appelé le règlement concernant l’information des consommateurs sur les denrées alimentaires, ou règlement INCO. En tant que règlement européen, il s’applique directement dans tous les États membres, y compris la Roumanie, sans transposition nationale nécessaire.',
      'Il s’applique depuis le 13 décembre 2014. L’article 9 énumère les informations que les denrées alimentaires doivent porter, l’article 21 précise comment les allergènes doivent être mis en évidence sur les denrées préemballées, et l’annexe II est la liste des 14 allergènes eux-mêmes.',
      'Pour les denrées non préemballées - les plats que vous cuisinez à la commande - l’article 44 rend obligatoire l’information sur les allergènes, mais laisse la méthode au choix de chaque pays. Les sanctions sont elles aussi fixées au niveau national, par la législation de sécurité alimentaire propre à chaque pays, et non par le règlement lui-même.',
    ],
    asksHeading: 'Ce qu’il exige de vous',
    asksItems: [
      'Nomme 14 allergènes que vous devez déclarer',
      'S’applique aux menus comme aux étiquettes',
      'Couvre les denrées préemballées comme celles servies non préemballées',
      'Doit être exact et tenu à jour',
    ],
    natashasLaw:
      'Une loi dont vous avez peut-être entendu parler, la Natasha’s Law britannique, est une règle du Royaume-Uni datant de 2021 - plus stricte que le socle européen, et qui ne fait pas du tout partie du droit de l’UE.',
  },
  steps: {
    eyebrow: 'Comment le déclarer',
    heading: 'Transformez l’obligation en une routine simple',
    intro: 'Ces 4 étapes vous font passer d’une pile de recettes à des informations sur les allergènes dignes de la confiance de vos clients.',
    items: [
      {
        title: 'Créez une source unique de référence',
        text: 'Notez lesquels des 14 chaque plat contient, à partir de vos recettes et fiches fournisseurs. Gardez tout au même endroit pour que la cuisine et la salle soient toujours d’accord.',
      },
      {
        title: 'Mettez-le là où les clients le voient',
        text: 'Montrez-le par écrit par défaut\u00A0: sur le menu, sur un tableau clair, ou sur un menu numérique. L’écrit vaut toujours mieux que de compter sur la mémoire de quelqu’un.',
      },
      {
        title: 'Formez votre équipe',
        text: 'Le personnel de salle doit lire l’information à partir du registre et savoir quand vérifier auprès de la cuisine plutôt que de deviner. Des réponses exactes, à chaque fois.',
      },
      {
        title: 'Tenez-le à jour',
        text: 'Chaque modification de recette ou nouveau fournisseur peut ajouter ou retirer un allergène. Mettez à jour le registre dès qu’un changement survient.',
      },
    ],
    footnote: 'Revérifiez à chaque changement de recette ou de fournisseur - c’est là que la plupart des menus deviennent obsolètes sans qu’on s’en aperçoive.',
  },
  comparison: {
    eyebrow: 'Où va l’information',
    heading: 'Denrées préemballées ou non préemballées',
    intro: 'Les mêmes 14 allergènes, déclarés à des endroits différents selon la façon dont la denrée est vendue. C’est la confusion que nous voyons le plus souvent.',
    leftTitle: 'Denrées préemballées',
    leftPoints: [
      'Vendue dans l’emballage dans lequel elle a été mise avant d’être commandée, comme une sauce en bouteille.',
      'Nécessite une liste complète des ingrédients, avec les 14 allergènes mis en évidence (article 21).',
      'Porte aussi les autres mentions d’étiquetage\u00A0: dénomination, quantité et date de durabilité.',
    ],
    rightTitle: 'Denrées servies non préemballées',
    rightPoints: [
      'Les plats que vous cuisinez et servez à la commande, ainsi que la nourriture emballée sur place à la demande d’un client.',
      'Aucune liste d’ingrédients imprimée n’est requise, mais l’information sur les allergènes est obligatoire (article 44).',
      'Donnée selon les modalités permises par votre pays - toujours exacte et facile à obtenir avant de commander.',
    ],
  },
  crossContact: {
    eyebrow: 'En cuisine',
    heading: 'Contamination croisée et «\u00A0peut contenir\u00A0»',
    imageAlt: 'Un plan de travail de restaurant propre, tout juste essuyé',
    bodyPre:
      'Déclarer les allergènes qui sont des ingrédients est une chose. La contamination croisée en est une autre - un allergène qui se déplace accidentellement via des friteuses, planches ou couteaux partagés. Une',
    bodyScribble: 'trace suffit',
    bodyPost:
      ', donc «\u00A0peut contenir\u00A0» n’est honnête qu’après avoir évalué le risque réel et constaté qu’il ne peut être supprimé.',
    tiles: [
      'Séparez préparation et ustensiles',
      'Nettoyez entre chaque commande',
      'Formez toute l’équipe',
      'Soyez honnête sur le «\u00A0peut contenir\u00A0»',
    ],
  },
  digital: {
    eyebrow: 'Là où un menu QR code aide',
    heading: 'Une modification, à jour sur toutes les tables',
    body: 'Les menus imprimés et les tableaux plastifiés partagent un même point faible\u00A0: les garder à jour. Un menu numérique élimine ce problème. Vous étiquetez chaque plat une seule fois, et une modification atteint chaque client immédiatement - sans réimpression.',
    list: [
      'Étiquetez les 14 allergènes par plat, une seule fois',
      'Les clients filtrent le menu selon ce qui est sûr pour eux',
      'Toujours la version à jour, dans leur propre langue',
    ],
    footnote: 'Honnête sur les limites\u00A0: un menu QR code ne rend pas vos données exactes et ne remplace pas la formation du personnel. Il corrige les informations obsolètes, ce qui est le problème le plus courant.',
    ctaLabel: 'Découvrez comment SafestMenu gère les allergènes',
    qrImageAlt: 'Une carte de table SafestMenu avec QR code à côté d’un café',
    dishImageAlt: 'Un plat dressé, présenté sur un menu numérique SafestMenu',
    journeyEyebrow: 'Le parcours de l’information sur les allergènes',
    journeyIntro: 'L’information sur les allergènes suit un parcours en 4 étapes. Votre menu - et un outil comme SafestMenu - se situe au point où elle atteint le client.',
  },
  faq: {
    eyebrow: 'Questions fréquentes',
    heading: 'Réponses rapides',
    items: [
      {
        q: 'Combien d’allergènes dois-je déclarer\u00A0?',
        a: 'Les 14. Le droit de l’UE fixe une liste dans l’annexe II du règlement 1169/2011, et tout plat qui en contient un doit le signaler. Vous pouvez signaler d’autres éléments demandés par vos clients, mais les 14 constituent le minimum légal.',
      },
      {
        q: 'Puis-je simplement dire aux clients de demander au personnel\u00A0?',
        a: 'Cela dépend de votre pays. Certains États membres autorisent l’information sur demande, à condition qu’un panneau clair invite les clients à demander et que le personnel donne des réponses exactes. D’autres, comme la France, l’exigent par écrit. L’écrit reste partout la solution la plus sûre par défaut.',
      },
      {
        q: 'La Natasha’s Law s’applique-t-elle à mon restaurant\u00A0?',
        a: 'Seulement si vous êtes au Royaume-Uni. La Natasha’s Law est une règle britannique du 1er octobre 2021 pour les denrées emballées sur place avant que le client ne commande, et elle est plus stricte que le socle européen. Elle ne s’applique ni en Roumanie ni dans le reste de l’UE.',
      },
      {
        q: 'L’huile d’arachide est-elle exemptée de déclaration\u00A0?',
        a: 'Non. L’huile d’arachide compte comme arachide, qu’elle soit raffinée ou non, elle doit donc toujours être déclarée. Seule l’huile de soja entièrement raffinée bénéficie de cette exemption, et «\u00A0huile de cacahuète\u00A0» ou «\u00A0huile d’arachis\u00A0» ne sont que d’autres noms pour l’huile d’arachide.',
      },
      {
        q: 'Qui est responsable si l’information sur les allergènes est erronée\u00A0?',
        a: 'Vous. Le restaurant est l’exploitant du secteur alimentaire, l’obligation de fournir une information exacte vous incombe donc, même si un fournisseur a modifié une recette sans vous prévenir. C’est pourquoi un registre unique et à jour est essentiel.',
      },
      {
        q: 'Un menu QR code suffit-il à lui seul à me rendre conforme\u00A0?',
        a: 'Non, et nous ne prétendrions jamais le contraire. Un menu numérique maintient vos informations sur les allergènes à jour sur toutes les tables à la fois, mais elles ne valent que ce que vous y avez saisi. Il vous faut toujours du personnel formé et une véritable démarche contre la contamination croisée en cuisine.',
      },
    ],
  },
  closingNote: {
    heading: 'Une note sur ce guide',
    body: 'Ceci est une orientation générale pour vous aider à vous organiser, et non un conseil juridique. Les règles nationales relevant de l’article 44 diffèrent selon les pays de l’UE et évoluent - vérifiez les règles en vigueur là où vous exercez, ou renseignez-vous auprès de votre autorité nationale de sécurité alimentaire.',
  },
  emailCapture: {
    heading: 'Gardez ce guide à portée de main',
    title: 'Recevez la checklist des allergènes par e-mail',
    description: 'Nous vous enverrons ce guide, plus quelques notes pratiques occasionnelles sur les menus et l’étiquetage des allergènes. Pas de spam, désabonnement à tout moment.',
    buttonLabel: 'Envoyez-le-moi',
  },
  cta: {
    heading: 'Mettez vos allergènes sur un menu digne de la confiance de vos clients',
    body: 'Étiquetez les 14 allergènes par plat, mettez à jour une seule fois, et chaque client voit le changement à table. Vous gardez un seul registre toujours à jour, et vos clients filtrent le menu selon ce qui est sûr pour eux.',
    buttonLabel: 'Essayez SafestMenu gratuitement',
  },
  diagrams: {
    journey: [
      { title: 'Le plat', sub: 'Ce qu’il contient vraiment, jusqu’à la sauce' },
      { title: 'Le registre', sub: 'Chaque plat associé aux 14 allergènes' },
      { title: 'Le menu', sub: 'Présenté au client, sur papier ou à l’écran', tag: 'SafestMenu' },
      { title: 'Le client', sub: 'Fait un choix en toute confiance' },
    ],
    routes: [{ label: 'Sur le menu' }, { label: 'Sur un panneau' }, { label: 'Indiqué sur demande' }],
    orLabel: 'ou',
    ingredients: {
      label: 'Ingrédients',
      segments: [
        { text: 'Farine de blé', mark: true },
        { text: ', eau, huile de colza, ' },
        { text: 'œuf', mark: true },
        { text: ', sucre, ' },
        { text: 'lait', mark: true },
        { text: ', sel, malt d’orge.' },
      ],
      note: 'Allergènes mis en évidence dans la liste des ingrédients.',
    },
  },
  reference: {
    ariaLabel: 'Les 14 allergènes de l’UE',
    allergenLabel: 'Allergène',
    commonlyIn: 'Se trouve souvent dans',
    hidesIn: 'Se cache dans',
    remember: 'À retenir',
    footnotePre: 'Touchez l’un des ',
    footnotePost: ' pour voir où il se cache. Les exemples sont un point de départ - vérifiez toujours vos propres recettes et fiches fournisseurs.',
  },
  items: {
    'cereals-gluten': {
      name: 'Gluten',
      short: 'Blé, seigle, orge, avoine, épeautre',
      common: 'Pain, pâtes, pâte à pizza, gâteaux, pâtisserie, pâte à frire, chapelure, couscous, bière, et la plupart des sauces et jus épaissis à la farine.',
      hidden: 'Sauce soja (généralement fermentée au blé), cubes de bouillon et granulés pour jus, vinaigre de malt, certaines saucisses et glaces (chapelure ou farine de blé comme liant), et mélanges d’épices.',
      note: 'L’avoine est naturellement sans gluten, mais presque toujours contaminée lors de la mouture. Ne considérez pas un plat à base d’avoine ordinaire comme sûr pour les cœliaques, sauf s’il est certifié sans gluten.',
    },
    crustaceans: {
      name: 'Crustacés',
      short: 'Crevettes, crabe, homard, langoustines',
      common: 'Crevettes, crabe, homard, langoustines, écrevisses et scampi.',
      hidden: 'Pâte de crevettes (belacan) dans les currys thaïlandais et d’Asie du Sud-Est, fumet de fruits de mer dans les soupes et risottos, bases de bouillabaisse et de paella, et crackers aux crevettes.',
      note: 'Une trace de fumet de crevettes dans une base de curry «\u00A0végétarien\u00A0» suffit à déclencher une réaction sévère. Vérifiez les woks, friteuses et marmites partagés.',
    },
    eggs: {
      name: 'Œuf',
      short: 'Mayonnaise, gâteaux, dorures, crème anglaise',
      common: 'Mayonnaise, quiche, omelettes, gâteaux, meringue, pâtes fraîches aux œufs, crème anglaise, et plats panés ou en pâte à frire.',
      hidden: 'Dorure à l’œuf badigeonnée sur la pâtisserie et le pain, consommés clarifiés au blanc d’œuf, mousses, guimauves, et mousses garnissant certains cocktails.',
      note: 'La dorure à l’œuf sur les produits de boulangerie est facile à manquer en lisant un menu rapidement. Signalez-la plutôt que de vous fier à la description de la pâtisserie.',
    },
    fish: {
      name: 'Poisson',
      short: 'Filets, anchois, sauces',
      common: 'Filets de poisson, bâtonnets de poisson, sushis, hachis de poisson, saumon fumé et anchois.',
      hidden: 'Sauce Worcestershire et sauce César (anchois), sauce de poisson dans les plats thaïlandais et vietnamiens, certaines sauces à pizza et à pâtes, mélanges à base de sauce huître, et cubes de bouillon.',
      note: 'La sauce de poisson et la sauce Worcestershire sont les deux sources classiques de poisson invisibles qui se glissent dans des plats par ailleurs à base de viande ou de légumes.',
    },
    peanuts: {
      name: 'Arachides',
      short: 'Cacahuète, satay, huile d’arachis',
      common: 'Beurre de cacahuète, sauce satay, arachides grillées et salées, huile d’arachide, et de nombreux plats asiatiques et africains.',
      hidden: 'Sauces au piment et au curry enrichies en arachides broyées, chocolats et desserts fabriqués sur des lignes partagées, et protéines végétales hydrolysées.',
      note: '«\u00A0Huile de cacahuète\u00A0» et «\u00A0huile d’arachis\u00A0» ne sont que d’autres noms pour l’huile d’arachide, et les deux doivent être déclarées. L’huile d’arachide raffinée n’est pas exemptée - seule l’huile de soja entièrement raffinée l’est.',
    },
    soybeans: {
      name: 'Soja',
      short: 'Sauce soja, tofu, lécithine',
      common: 'Sauce soja, tofu, edamame, miso, lait de soja et tempeh.',
      hidden: 'De nombreuses sauces et marinades asiatiques (hoisin, teriyaki), mélanges d’huiles végétales, viandes transformées avec liant au soja, certains pains, et lécithine de soja (E322).',
      note: 'La lécithine de soja (E322) se trouve dans une grande partie des chocolats, pâtes à tartiner et produits de boulangerie. Vérifiez les fiches fournisseurs, pas seulement l’ingrédient visible.',
    },
    milk: {
      name: 'Lait',
      short: 'Fromage, beurre, crème, petit-lait',
      common: 'Lait, fromage, beurre, crème, yaourt et glace.',
      hidden: 'Lait en poudre dans certains pains et pâtes, protéines de lait comme liant dans les viandes transformées, assaisonnement au petit-lait sur les chips, caséinate de sodium dans les garnitures «\u00A0non laitières\u00A0», et sauces à base de roux.',
      note: 'Les protéines de lait (caséine ou petit-lait) apparaissent dans des produits étiquetés «\u00A0non laitiers\u00A0» et dans le pain. Ne présumez jamais qu’un pain nature ou un assaisonnement sec est sans lactose sans vérifier.',
    },
    'tree-nuts': {
      name: 'Fruits à coque',
      short: 'Amande, noisette, noix, noix de cajou',
      common: 'Amandes, noisettes, noix, noix de cajou, noix de pécan, noix du Brésil, pistaches et noix de macadamia - dans les gâteaux, le pesto, les huiles et laits végétaux, et le pralin.',
      hidden: 'Pesto et sauces à salade, huiles de fruits à coque utilisées pour la friture ou en finition, korma et autres sauces épaissies aux fruits à coque broyés, massepain et frangipane, granola, et liqueurs aux fruits à coque.',
      note: 'Les lattes au lait d’amande et les pâtes au pesto sont deux pièges faciles à manquer. Tous deux cachent des fruits à coque derrière un nom qui ne dit pas «\u00A0noix\u00A0».',
    },
    celery: {
      name: 'Céleri',
      short: 'Céleri-rave, bouillon, assaisonnement',
      common: 'Branches de céleri, céleri-rave, soupes, ragoûts et salades.',
      hidden: 'Cubes de bouillon (le sel de céleri en est une base courante), granulés pour jus, mélanges d’épices et sels assaisonnés, certaines saucisses, et sauces toutes prêtes.',
      note: 'Le sel de céleri se cache dans presque tous les cubes de bouillon et mélanges d’assaisonnement du commerce. Vérifiez le bouillon de base avant de déclarer une soupe ou un jus sans céleri.',
    },
    mustard: {
      name: 'Moutarde',
      short: 'Condiment, sauces, assaisonnements secs',
      common: 'Moutarde en condiment, moutarde en poudre, sauces à salade et piccalilli.',
      hidden: 'Mayonnaise et de nombreuses sauces du commerce, marinades et épices à barbecue, poudres de curry et mélanges d’épices, certains pains, et viandes transformées.',
      note: 'La moutarde est un ingrédient discret dans la mayonnaise et les marinades. Elle apparaît rarement dans le nom du plat, mais se trouve très souvent dans la sauce.',
    },
    sesame: {
      name: 'Sésame',
      short: 'Pains, tahini, houmous',
      common: 'Graines de sésame sur le pain et les petits pains, tahini, houmous, halva, huile de sésame et falafels.',
      hidden: 'Graines cuites sous les petits pains, mélanges d’épices (za’atar, dukkah), sauces à base de tahini, et friteuses partagées avec des produits panés au sésame.',
      note: 'Le sésame est souvent cuit sous le petit pain, et pas seulement saupoudré dessus. Un coup d’œil rapide au pain peut le manquer.',
    },
    sulphites: {
      name: 'Dioxyde de soufre et sulfites',
      short: 'Vin, fruits secs, vinaigre',
      common: 'Vin (en particulier le blanc et le rosé), fruits secs comme les abricots et les raisins, sirop, jus de fruits et cidre.',
      hidden: 'Viandes transformées et saucisses, légumes en saumure et conserves, certains produits à base de pommes de terre précoupées et surgelées, sauces à base de vinaigre, et certaines boissons non alcoolisées.',
      note: 'Le seuil légal est une concentration supérieure à 10 mg/kg ou 10 mg/L, pas la simple présence. Les abricots secs et le vin bon marché sont les deux sources les plus souvent oubliées.',
    },
    lupin: {
      name: 'Lupin',
      short: 'Farine de lupin, mélanges «\u00A0sans\u00A0»',
      common: 'Farine de lupin et graines de lupin (lupini), ainsi que certains pains et produits de boulangerie sans gluten.',
      hidden: 'Mélanges de farine de blé et préparations de boulangerie utilisant le lupin comme complément protéiné, certaines pâtes, substituts de viande végétaliens, et pains et pâtisseries continentaux.',
      note: 'Le lupin est rare dans la cuisine britannique traditionnelle, mais courant dans les pains continentaux et les mélanges de farine sans gluten. Vérifiez toute farine importée ou «\u00A0sans\u00A0».',
    },
    molluscs: {
      name: 'Mollusques',
      short: 'Moules, huîtres, calamar, escargots',
      common: 'Moules, palourdes, huîtres, coquilles Saint-Jacques, calamar, poulpe et escargots.',
      hidden: 'Sauce huître dans les sautés et marinades, fumet de fruits de mer dans les bases de paella et de risotto, encre de calamar colorant les pâtes ou le riz, et surimi utilisé comme extenseur de fruits de mer.',
      note: 'La sauce huître est le mollusque caché classique. Elle apparaît dans des sautés et marinades qui semblent par ailleurs uniquement à base de viande ou de légumes.',
    },
  },
};
